"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type UseAsyncResult = ReturnType<typeof useAsync>;

type AsyncState = {
  error: string;
  isPending: boolean;
  isError: boolean;
  isSuccess: boolean;
  isDone: boolean;
};

const defaultState: AsyncState = {
  error: "",
  isPending: false,
  isError: false,
  isSuccess: false,
  isDone: false,
};

/**
 * Provides state for async function calls
 * @param fn - The async function to execute, it should return a value
 * @param dependencies - The `fn` will be memoized based on these dependencies (just like useCallback)
 * @param immediate - Whether to run `fn` immediately (or when dependencies change)
 * @return details.error - The error caught when trying to invoke `fn`
 * @return details.result - The resolved value from `fn`
 * @return details.isPending - `fn` has been invoked but is not yet completed
 * @return details.isDone - `fn` has been and invoked and is done (true for both reject and resolve)
 * @return details.isError - `fn` has been invoked but threw an error
 * @return details.isSuccess - `fn` has resolved with a value
 * @return details.execute - memoized version of `fn` that can be invoked as needed
 */
export function useAsync<T, D extends unknown[]>(
  fn: () => Promise<T>,
  dependencies?: D,
  immediate = true,
) {
  const [state, setState] = useState<AsyncState>({ ...defaultState });
  const [result, setResult] = useState<T>();
  /** The most recent execution's promise, used to prevent old calls resolving */
  const activePromise = useRef<Promise<T>>(undefined);
  /** Makes and handles the async call */
  const execute = useCallback(() => {
    setResult(undefined);
    setState({
      ...defaultState,
      isPending: true,
    });
    const promise = fn();
    activePromise.current = promise;
    promise
      .then((fnResult) => {
        if (promise === activePromise.current) {
          setResult(fnResult);
          setState({
            ...defaultState,
            isDone: true,
            isSuccess: true,
          });
        }
      })
      .catch((error) => {
        if (promise === activePromise.current) {
          setState({
            ...defaultState,
            error,
            isDone: true,
            isError: true,
          });
        }
      });
    return promise;
  }, dependencies ?? []);

  useEffect(() => {
    if (immediate) {
      execute();
    }
    return () => {
      activePromise.current = undefined;
    };
  }, [execute, immediate]);

  return useMemo(
    () => ({
      ...state,
      result,
      execute,
    }),
    [result, state, execute],
  );
}
